/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Typography, CircularProgress, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';

import {
  fetchDataFromS3Bucket,
  formatDate,
  getIpAddress,
  getStoredCustomerDetails,
  uploadDocumentToS3Bucket,
} from '../../../../Shared/Utilities/utils';
import { Patterns } from '../../../../Shared/Constants';
import {
  BILLING_INSTRUCTION_FIELDS,
  Paths,
  PO_ATTACHMENTS_FILE_CONTEXT,
  PO_LABEL,
} from '../../../../Shared/Constants/App';

import {
  BidApprovalResponseModel,
  EmailTransactionDetailsModel,
  RESPONSE_CODES,
  TransactionStatus,
  ICommentModel,
  IPOAttachments,
  BillingInfo,
} from '../../Models/BidApproval.Model';
import { IDetailsProps } from '../../../Jobs/Models/AddEditOneTimeJob.Model';

import { CommenterType, KeyType } from '../../../../API';
import CustomerApiService from '../../Services/CustomerService';
import JobCreateApiService from '../../../Jobs/Services/JobCreateService';
import MasterDataApiService from '../../../../Shared/Services/MasterDataService';
import BidApprovalPopup from './BidApprovalPopup/BidApprovalPopup';
import BillingInfoPopup from './BillingInfoPopup/BillingInfoPopup';
import PhotosAndVideos from '../../../Jobs/Components/AddEditOneTimeJob/PhotosAndVideos';
import { triggerPendingCustomerOTJApprovalRefresh } from '../../../../Shared/Store/features/appStateSlice';
import MediaPopup from '../../../../Shared/Components/Common/MediaPopUp/MediaPopUp';

import { Icons } from '../../../../Shared/Constants/Icons';
import theme from '../../../../Shared/Themes/theme';

import { CenterAlignment, CustomerContainer } from '../../../../Shared/Components/Layout/styles';
import { Container, TitleWrapper } from '../../../../Shared/Components/Common/PageTitle/PageTitle.styles';
import {
  BottombarContainer,
  CongratsContainer,
  CongratsText,
  ReviewContainer,
  ReviewText,
  DetailText,
  buttonStyle,
  ProposalFields,
  DetailFields,
  MediaContainer,
  MediaText,
  Divider,
  ProposalContainer,
  JobVisualContainer,
} from '../../../Vendor/Components/CreateBid.Style';
import { BidApprovalContainer } from './BidApprovalEmail.style';
import { Loader } from '../../../Jobs/Components/JobView/JobsView.styles';
import { buttonStyles } from '../../../Onboarding/Components/Login/LoginPage.Style';
import { ErrorMsg } from '../../../Jobs/Components/AddEditOneTimeJob/AddEditOneTimeJob.Style';
import PageTitle from '../../../../Shared/Components/Common/PageTitle/PageTitle';

type MediaType = 'photo' | 'video';

const BidApprovalEmail = (): JSX.Element => {
  const { t } = useTranslation(['altTexts']);
  const location = useLocation();
  const queryParams = new URLSearchParams(location?.search);
  const emailTransactionId = queryParams.get('txId');
  const status = queryParams.get('status');
  const { bidId, isFromEmail = true, jobName } = location?.state ?? {};
  const [serviceCategories, setServiceCategories] = useState([]);
  const [summary, setSummary] = useState<BidApprovalResponseModel>();
  const [noteToCustomer, setNoteToCustomer] = useState('');
  const { button, buttonTextStyle } = buttonStyle;
  const [isBidApprovalLoading, setIsBidApprovalLoading] = useState(false);
  const [isApprovingBid, setIsApprovingBid] = useState(false);
  const [isRejectingBid, setIsRejectingBid] = useState(false);
  const [ipAddress, setIpAddress] = useState<string>('');
  const [emailTransactionDetails, setEmailTransactionDetails] = useState<EmailTransactionDetailsModel | object>({});
  const [customMessage, setCustomMessage] = useState('');
  const [customHeaderImg, setCustomHeaderImg] = useState(Icons.ErrorCautionImg);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showCtaButton, setShowCtaButton] = useState(true);
  const [showErrorView, setShowErrorView] = useState(false);
  const [isLogoLoading, setIsLogoLoading] = useState<boolean>(false);
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [isValidLogo, setIsValidLogo] = useState<boolean>(false);
  const [images, setImages] = useState<any[]>([]);
  const [videos, setVideos] = useState<any[]>([]);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [mediaType, setMediaType] = useState<MediaType>('photo');
  const [isMediaLoading, setIsMediaLoading] = useState<boolean>(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [isChecked, setIsChecked] = useState(false);
  const [specialInstructions, setSpecialInstructions] = useState<string>('');
  const [poNumber, setPoNumber] = useState<string>('');
  const [poAttachments, setPOAttachments] = useState<IPOAttachments[]>([]);
  const [isBillingPopupOpen, setIsBillingPopupOpen] = useState(false);
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  // PO + billing special-instructions text confirmed in the popup; forwarded to processBidReview.
  const confirmedSpecialInstructionsRef = useRef<string>('');
  const [pendingEmailApproval, setPendingEmailApproval] = useState<{
    emailTransactionId: string;
    ipAddress: string;
    isFromEmail: boolean;
  } | null>(null);

  const { displayStyle, displayRowStyle } = buttonStyle;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userId = '' } = getStoredCustomerDetails() || {};
  const customerId = summary?.jobs?.customerId || '';
  const jobId = summary?.jobId || '';

  const parsedSummaryAddress = useMemo(() => {
    const parts = (summary?.customerAddress ?? '').split('|').map((part) => part.trim());
    const addressLine1 = parts[0] ?? '';
    const addressLine2 = parts[1] ?? '';

    const cityStateZipSegment = parts.find((part) => /\b[A-Za-z]{2}\s+\d{5}/.test(part)) ?? '';
    const stateZip = cityStateZipSegment.match(/\b([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)\b/);
    const state = stateZip?.[1]?.toUpperCase() ?? '';
    const zip = stateZip?.[2] ?? '';

    const segments = cityStateZipSegment
      .split(',')
      .map((segment) => segment.trim())
      .filter(Boolean);
    const stateZipIndex = segments.findIndex((segment) => /^[A-Za-z]{2}\s+\d{5}/.test(segment));
    const city = stateZipIndex > 0 ? segments[stateZipIndex - 1] : '';

    return { addressLine1, addressLine2, city, state, zip };
  }, [summary?.customerAddress]);

  const billingDefaults: BillingInfo = useMemo(() => {
    const customerName = summary?.jobs?.customerName || '';
    // Account-holder username and structured address now come straight from bidProposalSummary
    // (works in the email flow too). The parsed pipe string is kept only as a fallback.
    const contactName = summary?.accountHolderName || customerName;
    // Split the contact name into first word (first name) and the rest (last name).
    const [contactFirstName = '', ...contactLastNameParts] = contactName.trim().split(/\s+/);
    // Keep only the street (up to the first comma) of the full formatted address for Address Line 1.
    const street = (summary?.customerAddressLine2 ?? '').split(',')[0].trim();
    return {
      companyName: customerName,
      billingContactFirstName: contactFirstName,
      billingContactLastName: contactLastNameParts.join(' '),
      billingContactEmail: summary?.jobs?.customerEmail || '',
      addressLine1: street || parsedSummaryAddress.addressLine1 || '',
      addressLine2: summary?.customerAddressLine1 || parsedSummaryAddress.addressLine2 || '',
      city: summary?.customerCity || parsedSummaryAddress.city || '',
      state: summary?.customerStateCode || parsedSummaryAddress.state || '',
      zip: summary?.customerPostalCode || parsedSummaryAddress.zip || '',
      ...(billingInfo ?? {}),
    };
  }, [summary, parsedSummaryAddress, billingInfo]);

  const handleDialogState = (): void => {
    setIsDialogOpen(false);
    setCustomMessage('');
  };

  const toggleCheckbox = () => {
    setIsChecked?.(!isChecked);
  };

  const { handleSubmit, control, reset } = useForm({
    defaultValues: {
      comments: '',
      isFromEmail: isFromEmail ?? true,
      bidId: bidId ?? '',
    },
  });

  const checkIfAlreadyApprovedRejected = (emailTransactionDetails: EmailTransactionDetailsModel) => {
    if (emailTransactionDetails.transactionStatus === TransactionStatus.Approved) {
      setCustomMessage('Job is already approved');
      setCustomHeaderImg(Icons.CongratulationsImg);
      setShowErrorView(true);
      setShowCtaButton(false);
      return true;
    }
    if (emailTransactionDetails.transactionStatus === TransactionStatus.Rejected) {
      setCustomMessage('Job is already rejected');
      setCustomHeaderImg(Icons.CongratulationsImg);
      setShowErrorView(true);
      setShowCtaButton(false);
      return true;
    }
  };
  const checkIfImageValid = async (url: string) => {
    return new Promise((resolve) => {
      const image = new Image();
      image.src = url;
      image.onload = (): void => {
        resolve(true);
      };
      image.onerror = (): void => {
        resolve(false);
      };
    });
  };

  const handleDeleteAttachment = (index: number) => {
    const item = poAttachments?.[index];
    const filteredDetails = poAttachments?.filter((data) => data?.id !== item?.id);
    setPOAttachments(filteredDetails);
  };

  const fetchLogoUrl = async (vendorId: string) => {
    try {
      setIsLogoLoading(true);
      const url = await fetchDataFromS3Bucket(navigate, `vendor/${vendorId}/company-logos/${vendorId}`);
      const isValid = await checkIfImageValid(url);
      if (isValid) {
        setIsValidLogo(true);
        setLogoUrl(url);
      } else {
        setIsValidLogo(false);
        setLogoUrl('');
      }
      return { logoUrl, isValid };
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setIsLogoLoading(false);
    }
  };

  const rendersVendorLogo = (): JSX.Element | null => {
    if (isLogoLoading) {
      return <CircularProgress size={20} sx={{ color: theme.palette.primary.dark }} />;
    } else if (isValidLogo) {
      return <img src={logoUrl} style={{ height: '5rem', aspectRatio: '16/9' }} alt={t('altTexts:companyLogo')} />;
    }
    return null;
  };

  const isReadonlyProposal = (emailTransactionDetails: EmailTransactionDetailsModel) => {
    if (emailTransactionDetails?.isReadOnly) {
      setShowCtaButton(false);
    }
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const fetchBidSummary = async () => {
    try {
      let emailTransactionDetailsResponse;
      const isSummaryAlreadyLoading = localStorage.getItem('isCustomerApprovalInProgress');
      if (isSummaryAlreadyLoading === 'true') {
        return true;
      }
      setIsBidApprovalLoading(true);
      if (isFromEmail) {
        if (!emailTransactionId) {
          setCustomMessage('Invalid url');
          setCustomHeaderImg(Icons.ErrorCautionImg);
          setShowErrorView(true);
          setShowCtaButton(false);
          throw new Error('no email transaction id found in query params');
        }

        emailTransactionDetailsResponse = await CustomerApiService.getEmailTransactionDetails(emailTransactionId);
        if (
          !emailTransactionDetailsResponse?.data ||
          !Object.values(emailTransactionDetailsResponse?.data).some((field) => field !== null)
        ) {
          setCustomMessage('Something went wrong!');
          setCustomHeaderImg(Icons.ErrorCautionImg);
          setShowErrorView(true);
          setShowCtaButton(false);
          throw new Error('no email transaction details found for email transaction');
        }
        isReadonlyProposal(emailTransactionDetailsResponse?.data as EmailTransactionDetailsModel);
        if (checkIfAlreadyApprovedRejected(emailTransactionDetailsResponse?.data as EmailTransactionDetailsModel)) {
          throw new Error('already approved/rejected');
        }
        setEmailTransactionDetails(emailTransactionDetailsResponse.data);
      }
      const ipAddress = await getIpAddress();
      if (!ipAddress || !Object.keys(ipAddress)?.length) {
        setCustomMessage('Something went wrong!');
        setCustomHeaderImg(Icons.ErrorCautionImg);
        setShowErrorView(true);
        setShowCtaButton(false);
        throw new Error('Cannot find ip address');
      }
      const response = await CustomerApiService.getBidProposalSummary(
        isFromEmail ? (emailTransactionDetailsResponse?.data as EmailTransactionDetailsModel)?.bidId : bidId
      );

      if (isEmpty(response?.data)) {
        setShowCtaButton(false);
        throw new Error('Failed to fetch summary');
      }
      const commentsResponse = await JobCreateApiService.getComments(response?.data?.bidId);
      if (!commentsResponse?.data) {
        throw new Error('failed to fetch notes for customer');
      }
      const filteredComment: ICommentModel = commentsResponse.data.find(
        (comment: ICommentModel) =>
          (comment?.keyType as KeyType) === KeyType.JobBid &&
          (comment?.commenterType as CommenterType) === CommenterType.DistrictManager
      );
      fetchLogoUrl(response?.data?.vendorId);
      const { data: orgServiceCategoriesResponse } = await MasterDataApiService.getAllServiceCategoryV3(
        response?.data?.jobs?.customerId
      );
      if (response?.data?.jobId) {
        fetchAllJobAttachments(response?.data?.jobId);
      }

      if (!orgServiceCategoriesResponse?.length) {
        throw new Error('Failed to fetch services');
      }
      const serviceCategoriesList = response?.data?.jobs?.jobServices.map(
        (item: { mdServiceId: string }) => item?.mdServiceId
      );
      const filteredServiceCategories = orgServiceCategoriesResponse.reduce(
        (
          accumulator: string[],
          current: {
            taskId: string;
            serviceCategoryId: string;
            serviceCategoryName: string;
            categoryImageUrl: string;
            categoryDescription: string;
            orgId: string;
            mdServiceType: string;
            taskName: string;
            taskImageUrl: string;
            taskDescription: string;
            unit: string;
            mdAreaType: string;
            modifiedBy: string;
            createdBy: string;
          }
        ) => {
          if (
            serviceCategoriesList.includes(current?.serviceCategoryId) &&
            !accumulator?.includes(current?.serviceCategoryName)
          ) {
            accumulator.push(current?.serviceCategoryName);
          }
          return accumulator;
        },
        []
      );

      setServiceCategories(filteredServiceCategories);
      setSummary({
        ...response?.data,
      });
      const jobSummaryResponse = await JobCreateApiService.jobSummaryV2(
        response?.data?.customerId ?? '1234',
        response?.data?.jobId ?? jobId
      );
      if (jobSummaryResponse?.data) {
        setSpecialInstructions(jobSummaryResponse?.data?.specialInstructions ?? '');
      }
      setNoteToCustomer(filteredComment?.comment || '');
      if (emailTransactionId && status === TransactionStatus.Approved) {
        const emailTxnId = (emailTransactionDetailsResponse?.data as EmailTransactionDetailsModel)?.emailTransactionId;
        setPendingEmailApproval({
          emailTransactionId: emailTxnId,
          ipAddress: ipAddress?.ip,
          isFromEmail,
        });
        setIsBillingPopupOpen(true);
      }
      setIpAddress(ipAddress?.ip);
      setIsBidApprovalLoading(false);
    } catch (error) {
      console.log('entering into this catch block');
      localStorage.setItem('isCustomerApprovalInProgress', 'false');
      console.error(error);
      setIsBidApprovalLoading(false);
    }
  };
  // Section markers we manage on the special instructions. The customer's own free-text is kept;
  // our appended PO + BILL TO ADDRESS + Service Location block is rebuilt so re-runs don't duplicate.
  const oldBillingLabelPrefixes = BILLING_INSTRUCTION_FIELDS.map((field) => `${field.label}:`);
  const managedSectionMarkers = [`${PO_LABEL}:`, 'BILL TO ADDRESS:', 'Service Location:'];

  const baseInstructions = (() => {
    let lines = specialInstructions.split('\n');
    // Drop everything from the first managed section marker onward (our previously-appended block).
    const cutIndex = lines.findIndex((line) => managedSectionMarkers.some((marker) => line.trim().startsWith(marker)));
    if (cutIndex !== -1) {
      lines = lines.slice(0, cutIndex);
    }
    // Drop any stray old-format billing label lines (Company Name:, Address Line 1:, City:, ...).
    lines = lines.filter((line) => !oldBillingLabelPrefixes.some((prefix) => line.trim().startsWith(prefix)));
    return lines.join('\n').trim();
  })();

  // Service location = the job's customer/service address from the bid summary (deduped pipe parts).
  const serviceLocationAddress = Array.from(
    new Set(
      (summary?.customerAddress ?? '')
        .split('|')
        .map((part) => part.trim())
        .filter(Boolean)
    )
  ).join(', ');

  // Builds the special-instructions text: existing instructions (preserved) + PO number +
  // BILL TO ADDRESS block + Service Location block. Sent to processBidReview on approval.
  const buildSpecialInstructions = (info: BillingInfo): string => {
    const contactName = [info.billingContactFirstName, info.billingContactLastName].filter(Boolean).join(' ').trim();
    const cityStateZip = [info.city, [info.state, info.zip].filter(Boolean).join(' ').trim()]
      .filter(Boolean)
      .join(', ');
    const billingAddress = [info.addressLine1, info.addressLine2, cityStateZip].filter(Boolean).join(', ');

    const billToBlock = [
      'BILL TO ADDRESS:',
      info.companyName ? `${info.companyName},` : '',
      contactName,
      info.billingContactEmail,
      billingAddress,
    ]
      .filter(Boolean)
      .join('\n');

    const sections: string[] = [];
    if (baseInstructions) sections.push(baseInstructions);
    if (poNumber) sections.push(`${PO_LABEL}: ${poNumber}`);
    sections.push(billToBlock);
    if (serviceLocationAddress) sections.push(`Service Location:\n${serviceLocationAddress}`);
    return sections.join('\n\n');
  };

  useEffect(() => {
    if (!specialInstructions) return;
    const lines = specialInstructions?.split('\n');
    const poLine = lines?.find((line) => line?.startsWith(`${PO_LABEL}:`));
    if (poLine) {
      const extractedPo = poLine.replace(`${PO_LABEL}:`, '').trim();
      setPoNumber(extractedPo);
    }
  }, [specialInstructions]);

  useEffect(() => {
    fetchBidSummary();
    handleTermsAndConditions();
  }, []);

  const handleApprovalSubmission = async (data: { isFromEmail: boolean; comments: string; bidId: string }) => {
    await handleApproval(
      (emailTransactionDetails as EmailTransactionDetailsModel)?.emailTransactionId ?? '',
      ipAddress,
      data?.isFromEmail,
      data?.comments,
      summary?.jobId,
      data?.bidId
    );
  };

  // Opens the billing confirmation popup for the on-page "Approve" action.
  const handleApproveClick = () => {
    setPendingEmailApproval(null);
    setIsBillingPopupOpen(true);
  };

  const handleBillingCancel = () => {
    setIsBillingPopupOpen(false);
  };

  // Runs only after the customer confirms their billing information.
  const handleBillingConfirm = async (confirmedBillingInfo: BillingInfo) => {
    setBillingInfo(confirmedBillingInfo);
    // Capture the string so handleApproval forwards it to processBidReview (which persists it).
    confirmedSpecialInstructionsRef.current = buildSpecialInstructions(confirmedBillingInfo);
    if (pendingEmailApproval) {
      localStorage.setItem('isCustomerApprovalInProgress', 'true');
      await handleApproval(
        pendingEmailApproval.emailTransactionId,
        pendingEmailApproval.ipAddress,
        pendingEmailApproval.isFromEmail
      );
      setPendingEmailApproval(null);
    } else {
      await handleSubmit(handleApprovalSubmission)();
    }
  };

  const rendersCustomerAddress = () => {
    if (summary?.customerAddress) {
      const splitAddress = summary?.customerAddress.split('|');
      if (splitAddress.length > 2 && splitAddress[2]?.trim()) {
        return splitAddress[2].trim();
      }
      return splitAddress[0].trim();
    }
  };

  const handleSuccessDialog = (responseCode: string) => {
    if (responseCode === RESPONSE_CODES.BIDS.RECEIVED_APPROVAL) {
      setCustomMessage('Thank you for your approval!');
      setCustomHeaderImg(Icons.CongratulationsImg);
      dispatch(triggerPendingCustomerOTJApprovalRefresh());
    } else {
      setCustomMessage('Expired');
      setCustomHeaderImg(Icons.ErrorCautionImg);
    }
    setIsDialogOpen(true);
    setIsApprovingBid(false);
    setShowCtaButton(false);
    setIsBillingPopupOpen(false);
  };

  const handleAttachmentDetails = (data: IPOAttachments[]) => {
    const modifiedAttachments = data?.map((attachment: IPOAttachments) => {
      return {
        fileName: attachment?.fileName,
        fileExtension: attachment?.fileExtension,
        facilityId: summary?.jobs?.jobFacilities?.[0]?.facilityId ?? '',
        mdFileContext: PO_ATTACHMENTS_FILE_CONTEXT,
        mdAttachmentType: attachment?.mdAttachmentType,
        url: attachment?.url,
        id: attachment?.id,
      };
    });
    setPOAttachments(modifiedAttachments);
  };

  const handleApproval = async (
    emailTransactionId: string,
    ipAddress: string,
    isFromEmail: boolean,
    comments?: string,
    jobId?: string,
    bidId?: string
  ) => {
    try {
      setIsApprovingBid(true);
      let modifiedAttachments: IPOAttachments[] = [];
      if (!isEmpty(poAttachments)) {
        modifiedAttachments = await Promise.all(
          poAttachments.map(async (attachment) => {
            const s3BucketPath = `customer/${customerId}/po-attachments/${jobId}/${attachment.id}${
              attachment?.fileExtension ? `.${attachment?.fileExtension}` : ''
            }`;

            const s3Key = await uploadDocumentToS3Bucket(
              navigate,
              s3BucketPath,
              attachment?.url,
              {
                level: 'public',
              },
              attachment?.fileExtension
            );
            return {
              ...attachment,
              url: s3Key?.file?.key,
            };
          })
        );
      }

      const response = await CustomerApiService.acceptRejectBidProposal(
        emailTransactionId ?? '',
        TransactionStatus.Approved,
        {
          userId,
          ipAddress,
          comments: comments ?? '',
          isFromEmail,
          jobId,
          bidId,
          attachments: modifiedAttachments,
          specialInstructions: confirmedSpecialInstructionsRef.current,
        }
      );
      if (comments && summary?.bidId) {
        const commentsInput = {
          createdBy: summary?.jobs?.customerId ?? '',
          keyType: KeyType.JobBid,
          comment: comments ?? '',
          commenterType: CommenterType.Customer,
        };
        const commentsResponse = await JobCreateApiService.addComments(summary?.bidId ?? '', commentsInput);
        if (!commentsResponse?.data) {
          throw new Error('failed to add comment');
        }
      }
      if (!response?.data?.responseCode) {
        throw new Error('no response found');
      }
      reset({
        comments: '',
      });
      handleSuccessDialog(response?.data?.responseCode);
      localStorage.setItem('isCustomerApprovalInProgress', 'false');
    } catch (error) {
      console.error('Error', error);
      setIsApprovingBid(false);
      localStorage.setItem('isCustomerApprovalInProgress', 'false');
    }
  };

  const handleTermsAndConditions = async () => {
    try {
      const { pdfUrl } = await CustomerApiService.fetchTermsAndConditions();
      setPdfUrl(pdfUrl);
    } catch (error) {
      console.error('Error fetching terms and conditions', error);
    }
  };

  const handleRejectDialog = (responseCode: string) => {
    if (responseCode === RESPONSE_CODES.BIDS.RECEIVED_REJECTION) {
      setCustomMessage('Quote sent for revision. Thank you!');
      setCustomHeaderImg(Icons.CongratulationsImg);
      dispatch(triggerPendingCustomerOTJApprovalRefresh());
    } else {
      setCustomMessage('Expired');
      setCustomHeaderImg(Icons.ErrorCautionImg);
    }
    setIsDialogOpen(true);
    setIsRejectingBid(false);
    setShowCtaButton(false);
  };

  const handleReject = async (data: { comments: string; bidId?: string }) => {
    try {
      setIsRejectingBid(true);
      const response = await CustomerApiService.acceptRejectBidProposal(
        (emailTransactionDetails as EmailTransactionDetailsModel)?.emailTransactionId ?? '',
        TransactionStatus.Rejected,
        { userId, ipAddress, isFromEmail, comments: data?.comments, jobId: summary?.jobId, bidId: data?.bidId }
      );
      if (data?.comments && summary?.bidId) {
        const commentsInput = {
          createdBy: summary?.bidId,
          keyType: KeyType.JobBid,
          comment: data?.comments,
          commenterType: CommenterType.Customer,
        };
        const commentsResponse = await JobCreateApiService.addComments(summary?.bidId, commentsInput);
        if (!commentsResponse?.data) {
          throw new Error('failed to add comment');
        }
      }
      if (!response?.data?.responseCode) {
        throw new Error('not response found');
      }
      reset({
        comments: '',
      });
      handleRejectDialog(response?.data?.responseCode);
    } catch (error) {
      console.error('Error', error);
      setIsRejectingBid(false);
    }
  };

  const { textField } = buttonStyles;

  const rendersCallToActionButton = () => {
    if (showCtaButton) {
      return (
        <BottombarContainer>
          <Button
            sx={{ ...button, opacity: isApprovingBid || isRejectingBid || !isChecked ? '0.5' : '1' }}
            onClick={handleApproveClick}
            disabled={isApprovingBid || !isChecked}>
            {isApprovingBid ? (
              <CircularProgress size={30} sx={{ color: theme.palette.common.white }} />
            ) : (
              <Typography sx={{ ...buttonTextStyle, textTransform: 'none' }}>Approve</Typography>
            )}
          </Button>
          <Button
            sx={{ ...button, opacity: isApprovingBid || isRejectingBid ? '0.5' : '1' }}
            disabled={isRejectingBid}
            onClick={handleSubmit(handleReject)}>
            {isRejectingBid ? (
              <CircularProgress size={30} sx={{ color: theme.palette.common.white }} />
            ) : (
              <Typography sx={{ ...buttonTextStyle, textTransform: 'none' }}>Request Revision</Typography>
            )}
          </Button>
        </BottombarContainer>
      );
    }
    return null;
  };

  const navigateToPendingCustomerApproval = () => {
    navigate(Paths.PendingCustomerApprovalOneTimeJobs);
  };

  const rendersErrorView = () => {
    return (
      <CenterAlignment>
        <CongratsContainer sx={{ borderRadius: '1.5rem', marginTop: '80px', width: '35rem', padding: '1rem' }}>
          <Box sx={{ margin: '3rem 0 2.5rem 0' }}>
            <img src={customHeaderImg} />
          </Box>
          <CongratsText sx={{ textAlign: 'center' }}>{customMessage}</CongratsText>
          {!isFromEmail ? (
            <Button sx={{ ...button }} onClick={() => navigateToPendingCustomerApproval()} disabled={false}>
              <Typography sx={{ ...buttonTextStyle, textTransform: 'none' }}>
                {t('homePage:goBackToPendingCustomerApprovalJobs')}
              </Typography>
            </Button>
          ) : null}
        </CongratsContainer>
      </CenterAlignment>
    );
  };

  const openMediaPopup = (type: MediaType) => {
    const mediaArray = type === 'photo' ? images : videos;
    if (mediaArray.length) {
      setPopupOpen(true);
      setMediaType(type);
    }
  };

  const rendersMainContent = () => {
    return (
      <>
        <Container sx={{ width: '100%' }}>
          {isFromEmail ? (
            <TitleWrapper>
              <img src={Icons.PeazyNameLogo} alt={t('altTexts:peazyLogo')} />
            </TitleWrapper>
          ) : (
            <PageTitle title={jobName} />
          )}
        </Container>
        {showErrorView ? (
          <CustomerContainer>{rendersErrorView()}</CustomerContainer>
        ) : (
          <CustomerContainer>
            <ReviewContainer
              sx={{
                display: 'flex',
                flexDirection: 'column',
                marginTop: '1.5rem',
              }}>
              <Box sx={{ marginBottom: '0.5rem' }}>{rendersVendorLogo()}</Box>
              <DetailFields>
                <ReviewText>{summary?.vendorName}</ReviewText>
                <DetailText>{summary?.jobCustodianAddress}</DetailText>
              </DetailFields>
            </ReviewContainer>
            <ReviewContainer sx={buttonStyle?.displayStyle}>
              <ProposalContainer>
                <Box>
                  <Box
                    sx={{
                      ...displayStyle,
                      justifyContent: 'space-between',
                    }}>
                    <ReviewText sx={{ fontSize: theme.typography.h1?.fontSize }}>Proposal</ReviewText>
                    <ReviewText>Submitted for approval on {formatDate(summary?.bidSubmissionDate)}</ReviewText>
                  </Box>
                </Box>
                <DetailFields sx={{ width: '14rem' }}>
                  <ReviewText>Proposal for</ReviewText>
                  <DetailText>{summary?.jobs?.customerName}</DetailText>
                  <DetailText>{rendersCustomerAddress()}</DetailText>
                </DetailFields>
              </ProposalContainer>
              <ProposalFields
                sx={{
                  marginBottom: '1.5rem',
                }}>
                <DetailFields>
                  <ReviewText>Project</ReviewText>
                  <DetailText>{summary?.bidName}</DetailText>

                  <MediaPopup
                    isOpen={isPopupOpen}
                    onClose={closeMediaPopup}
                    currentMediaIndex={currentMediaIndex}
                    media={mediaType === 'photo' ? images : videos}
                    showPreviousMedia={() => showMedia('previous')}
                    showNextMedia={() => showMedia('next')}
                    numMedia={mediaType === 'photo' ? images?.length : videos?.length}
                    type={mediaType}
                  />
                </DetailFields>
                <DetailFields>
                  <ReviewText>Location</ReviewText>
                  <DetailText>{summary?.customerAddressName}</DetailText>
                </DetailFields>
                <DetailFields>
                  <ReviewText>{t('vendor:poNumber')}</ReviewText>
                  <TextField
                    size="small"
                    value={poNumber}
                    onChange={(e) => setPoNumber(e?.target?.value)}
                    sx={{
                      ...textField,
                      width: '60%',
                    }}
                  />
                </DetailFields>
              </ProposalFields>
              <ProposalFields>
                <DetailFields>
                  <ReviewText>Created by</ReviewText>
                  <DetailText>{summary?.jobs?.createdBy}</DetailText>
                </DetailFields>
                <DetailFields>
                  <ReviewText>Date Range for Completion</ReviewText>
                  <Box sx={{ ...displayRowStyle, gap: '2rem' }}>
                    <DetailText>{formatDate(summary?.jobs?.startDate)}</DetailText>
                    <DetailText>{formatDate(summary?.jobs?.stopDate)}</DetailText>
                  </Box>
                </DetailFields>
                <DetailFields>
                  <ReviewText>Approve by</ReviewText>
                  <DetailText>{formatDate(summary?.jobs?.startDate)}</DetailText>
                </DetailFields>
              </ProposalFields>
              <JobVisualContainer>
                <ReviewText sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  Job Visuals :{' '}
                  <span style={{ fontWeight: theme.typography.fontWeightBold, display: 'flex', marginLeft: '0.25em' }}>
                    {isMediaLoading ? (
                      <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
                    ) : (
                      // <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      <>
                        <MediaContainer onClick={() => openMediaPopup('photo')}>
                          <img src={Icons.PhotosIcon} alt={t('altTexts:galleryIcon')} />
                          <MediaText>{images?.length} Photos</MediaText>
                        </MediaContainer>
                        <Divider />
                        <MediaContainer onClick={() => openMediaPopup('video')}>
                          <img src={Icons.VideoIcon} alt={t('altTexts:videoIcon')} />
                          <MediaText>{videos?.length} Videos</MediaText>
                        </MediaContainer>
                      </>
                      // </Box>
                    )}
                  </span>
                </ReviewText>
              </JobVisualContainer>
            </ReviewContainer>
            <ReviewContainer sx={buttonStyle.displayStyle}>
              <ReviewText sx={{ fontSize: theme.typography.h2?.fontSize }}>Description</ReviewText>
              {serviceCategories.length ? (
                <DetailText sx={{ marginBottom: '1rem' }}>
                  <ul style={{ listStyleType: 'disc', paddingLeft: '1rem' }}>
                    {serviceCategories.map((category, index) => (
                      <li key={index}>
                        <Typography sx={{ fontWeight: theme.typography.fontWeightBold }}>{category}</Typography>
                      </li>
                    ))}
                  </ul>
                </DetailText>
              ) : null}
              <ReviewText
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '1rem',
                }}>
                Total Price:{' '}
                <span style={{ fontWeight: theme.typography.fontWeightBold }}>
                  ${summary?.finalQuote ? parseFloat(summary?.finalQuote.toString()).toFixed(2) : '0.00'}
                </span>
              </ReviewText>
              {noteToCustomer ? (
                <Box sx={buttonStyle.displayStyle}>
                  <ReviewText sx={{ display: 'flex', gap: '1rem' }}>
                    Notes:<DetailText>{noteToCustomer}</DetailText>
                  </ReviewText>
                </Box>
              ) : null}
              {showCtaButton ? (
                <>
                  <ReviewText sx={{ marginBottom: '1rem' }}>Comments:</ReviewText>
                  <Controller
                    control={control}
                    name={'comments'}
                    disabled={status === TransactionStatus.Approved}
                    render={({ field: { onChange, value }, fieldState: { error } }) => {
                      return (
                        <>
                          <TextField
                            multiline
                            sx={{
                              ...textField,
                              width: '100%',
                              '& .MuiOutlinedInput-input': {
                                color: theme.palette.text.primary,
                                fontSize: theme.typography.h5?.fontSize,
                                fontFamily: theme.typography.fontFamily,
                                fontWeight: theme.typography.fontWeightRegular,
                                lineHeight: '1.375rem',
                                letterSpacing: '0.015625rem',
                              },
                            }}
                            disabled={status === TransactionStatus.Approved}
                            onChange={(e) => onChange(e.target.value)}
                            value={value}></TextField>
                          <ErrorMsg>{error && error?.message}</ErrorMsg>
                        </>
                      );
                    }}
                    rules={{
                      pattern: {
                        value: Patterns.Instructions,
                        message: 'Invalid comments provided',
                      },
                      maxLength: {
                        value: 200,
                        message: 'Comments should be less than 200 characters',
                      },
                    }}
                  />
                </>
              ) : null}
              <Box sx={{ marginTop: 4 }}>
                <ReviewText
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                  {t('vendor:poAttachments')}
                  <PhotosAndVideos
                    label={t('vendor:poAttachments')}
                    customerId={customerId}
                    jobId={jobId}
                    details={poAttachments}
                    handleAttachmentDetails={handleAttachmentDetails}
                    handleDeleteAttachment={handleDeleteAttachment}
                    isEditPhotosAndVideos={true}
                    buttonSx={{ width: '17rem', padding: '0.3rem 1rem' }}
                    allowPdf={true}
                    allowVideo={false}
                  />
                </ReviewText>
              </Box>
              {pdfUrl && (
                <Box sx={{ display: 'flex', marginTop: '1rem', marginBottom: '1rem' }}>
                  <Button sx={{ padding: '0' }} onClick={() => window.open(pdfUrl, '_blank')}>
                    <Typography
                      sx={{
                        color: theme.palette.primary.dark,
                        textTransform: 'capitalize',
                      }}>
                      {t('vendor:viewTermsAndConditions')}
                    </Typography>
                  </Button>
                </Box>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'row', marginBottom: '1rem' }}>
                <Box onClick={toggleCheckbox}>
                  {isChecked ? (
                    <img
                      src={Icons.CheckedIcon}
                      alt={t('altTexts:checked')}
                      style={{ cursor: 'pointer', height: '1.5rem', width: '1.5rem' }}
                    />
                  ) : (
                    <img
                      src={Icons.Unchecked}
                      alt={t('altTexts:unchecked')}
                      style={{ cursor: 'pointer', height: '1.5rem', width: '1.5rem' }}
                    />
                  )}
                </Box>
                <Typography sx={{ marginLeft: '0.625rem' }}>{t('vendor:haveReadTermsAndConditions')}</Typography>
              </Box>
            </ReviewContainer>
          </CustomerContainer>
        )}
        {rendersCallToActionButton()}
      </>
    );
  };

  const rendersApprovalContent = () => {
    if (isBidApprovalLoading) {
      return (
        <Loader sx={{ height: '100vh' }}>
          <CircularProgress size={50} sx={{ color: theme.palette.primary.dark }} />
        </Loader>
      );
    }
    return rendersMainContent();
  };

  const showMedia = (direction: 'previous' | 'next') => {
    setCurrentMediaIndex((prevIndex) => {
      const mediaArray = mediaType === 'photo' ? images : videos;
      return direction === 'previous'
        ? (prevIndex - 1 + mediaArray?.length) % mediaArray?.length
        : (prevIndex + 1) % mediaArray?.length;
    });
  };

  const closeMediaPopup = () => {
    setPopupOpen(false);
    setCurrentMediaIndex(0);
  };

  const fetchAllJobAttachments = async (jobId: string) => {
    try {
      setIsMediaLoading(true);
      setImages([]);
      setVideos([]);
      const updatedJobId = jobId;
      const allJobAttachmentInput = { jobIds: [updatedJobId] };
      const attachmentResponse = await JobCreateApiService.getAllJobAttachmentsForBidSummary(allJobAttachmentInput);
      if (isEmpty(attachmentResponse.errors)) {
        const updatedDetails = await Promise.all(
          attachmentResponse.data.map(async (details: IDetailsProps) => {
            const attachmentUrl = await fetchDataFromS3Bucket(navigate, details?.url ?? '');
            const modifiedUrl = attachmentUrl.split('?x-amz-content');
            return {
              ...details,
              url: modifiedUrl?.[0],
              id: details?.attachmentId,
              attachmentId: details?.attachmentId,
              responseUrl: details?.url,
            };
          })
        );
        const imagesList: any[] = [];
        const videosList: any[] = [];
        updatedDetails.forEach((attachment: any) => {
          if (attachment.mdAttachmentType.includes('image')) {
            imagesList.push(attachment);
          } else {
            videosList.push(attachment);
          }
        });
        setImages([...images, ...imagesList]);
        setVideos([...videos, ...videosList]);
        setIsMediaLoading(false);
      } else {
        setIsMediaLoading(false);
      }
    } catch (e) {
      setIsMediaLoading(false);
      console.error('error', e);
    }
  };

  return (
    <BidApprovalContainer>
      {rendersApprovalContent()}
      <BillingInfoPopup
        open={isBillingPopupOpen}
        onCancel={handleBillingCancel}
        onConfirm={handleBillingConfirm}
        defaultValues={billingDefaults}
        isSubmitting={isApprovingBid}
      />
      <BidApprovalPopup
        open={isDialogOpen}
        onClose={handleDialogState}
        headerImg={customHeaderImg}
        message={customMessage}
        isFromEmail={isFromEmail}
      />
    </BidApprovalContainer>
  );
};

export default BidApprovalEmail;
